import type { Event } from '@viet-parenting/supabase/types';
import { countryName, getCountryByCode } from './countries';
import type { Locale } from './i18n';

type EventLike = Pick<Event, 'event_type' | 'venue_city' | 'venue_country'>;

/**
 * Online events have no venue. `event_type` is authoritative; the empty-city
 * check only covers rows written before the column existed.
 */
export function isOnlineEvent(ev: EventLike): boolean {
  return ev.event_type ? ev.event_type === 'online' : !ev.venue_city;
}

/** Human-readable place: "Trực tuyến" / "Online", or "City, Country". */
export function eventLocation(ev: EventLike, locale: Locale): string {
  if (isOnlineEvent(ev)) return locale === 'vi' ? 'Trực tuyến' : 'Online';
  const country = getCountryByCode(ev.venue_country);
  return [ev.venue_city, country ? countryName(country, locale) : null]
    .filter(Boolean)
    .join(', ');
}

const tzCache = new Map<string, string>();

/**
 * Fall back to UTC for missing or invalid IANA time zones. Admin-entered
 * values like `Europe/Belgium` are not real zones and make `toLocaleDateString`
 * throw, which fails the whole build during prerender.
 */
export function safeTimeZone(tz: string | null | undefined): string {
  if (!tz) return 'UTC';
  const cached = tzCache.get(tz);
  if (cached) return cached;
  let resolved = 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    resolved = tz;
  } catch {
    /* not a valid IANA zone — keep UTC */
  }
  tzCache.set(tz, resolved);
  return resolved;
}

/**
 * PostgREST filter for "events that belong on this country's pages":
 * the country's own events, global ones, plus every online event.
 */
export function countryEventsFilter(countryCode: string): string {
  return `venue_country.eq.${countryCode},venue_country.eq.global,event_type.eq.online`;
}

/** True once the event's end time has passed. */
export function isPastEvent(
  ev: Pick<Event, 'ends_at'>,
  now: number = Date.now(),
): boolean {
  return new Date(ev.ends_at).getTime() < now;
}
